import { injectable } from '~/decorators/dependency-container'
import { inject } from 'tsyringe'
import VueI18n from 'vue-i18n'
import { validator } from '~/decorators/form/validation'
import { Ref } from '~/utils/nuxt3-migration'
import { vueI18nToken } from '~/constants/dependency-injection/tokens'

@injectable()
export default class InternalFormValidationBuilder {
  private validators: any[] = []
  public error?: Ref<string | null | { [key: string]: any }>
  public key?: string
  constructor(@inject(vueI18nToken) private i18n: VueI18n) {}

  validate(): null | string {
    for (const validator of this.validators) {
      try {
        validator()
      } catch (e) {
        const errorMessage = (e as any).message
        if (this.error?.value && this.key) {
          ;(this.error.value as { [key: string]: any })[this.key] = errorMessage
        } else if (this.error) {
          this.error.value = errorMessage
        }

        return errorMessage
      }
    }
    if (this.error?.value && this.key) {
      ;(this.error.value as { [key: string]: any })[this.key] = null
    } else if (this.error) {
      this.error.value = null
    }
    return null
  }

  @validator()
  ipIsValid(ip: string): InternalFormValidationBuilder {
    if (
      ip &&
      !/^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/gm.test(
        ip
      )
    ) {
      throw new TypeError(this.i18n.t('please enter a valid ip').toString())
    }
    return this
  }

  @validator()
  public emailIsValid(emailAddress: string): InternalFormValidationBuilder {
    if (emailAddress && !emailAddress.includes('@')) {
      throw new TypeError(this.i18n.t('please enter a valid email').toString())
    }
    return this
  }

  @validator()
  minCharsWithFeedback(
    length: number,
    str: string | number,
    ignoreZeroLength: boolean = false
  ): InternalFormValidationBuilder {
    const strLength = str ? str.toString().length : null
    if (
      (length || length === 0) &&
      strLength &&
      strLength < length &&
      (!ignoreZeroLength || strLength !== 0)
    ) {
      const minCharactersRemaining = length - strLength
      throw new Error(
        this.i18n
          .t('you need at least {count} more characters', {
            count: minCharactersRemaining
          })
          .toString()
      )
    }
    return this
  }

  @validator()
  minChars(
    length: number,
    str: string | number,
    ignoreZeroLength: boolean = false
  ): InternalFormValidationBuilder {
    const strLength = str ? str.toString().length : null
    if (
      (length || length === 0) &&
      strLength &&
      strLength < length &&
      (!ignoreZeroLength || strLength !== 0)
    ) {
      throw new Error(
        this.i18n.tc(
          'requires at least 1 character | requires at least {count} characters',
          length
        )
      )
    }
    return this
  }

  @validator()
  required(
    isRequired: boolean,
    value: any,
    isActive: boolean = true
  ): InternalFormValidationBuilder {
    if (isRequired && isActive) {
      if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.length === 0) ||
        (typeof value === 'object' && value.length === 0)
      ) {
        throw new TypeError(this.i18n.t('this field is required').toString())
      }
    }
    return this
  }

  @validator()
  aboveMinNumber(
    minNumber: number,
    number: any,
    isActive: boolean = true
  ): InternalFormValidationBuilder {
    if (
      isActive &&
      typeof number === 'number' &&
      Number.isFinite(minNumber) &&
      number < minNumber
    ) {
      throw new TypeError(
        this.i18n
          .t('this field can not be smaller than {value}', {
            value: minNumber
          })
          .toString()
      )
    }
    return this
  }

  @validator()
  belowMaxNumber(
    maxNumber: number,
    number: any,
    isActive: boolean = true
  ): InternalFormValidationBuilder {
    if (
      isActive &&
      typeof number === 'number' &&
      Number.isFinite(maxNumber) &&
      number > maxNumber
    ) {
      throw new Error(
        this.i18n
          .t('this field can not be larger than {value}', {
            value: maxNumber
          })
          .toString()
      )
    }
    return this
  }

  @validator()
  isNumeric(value: any): InternalFormValidationBuilder {
    if (typeof value === 'string') {
      value = value.replace(',', '.')
    }
    if (isNaN(value)) {
      throw new TypeError(this.i18n.t('this field must be a number').toString())
    }
    return this
  }

  @validator()
  arrayMinLength(length: number, array: any[]): InternalFormValidationBuilder {
    if (array && array.length < length) {
      throw new TypeError(
        this.i18n
          .t('this field must have at least {count} values', {
            count: length
          })
          .toString()
      )
    }
    return this
  }

  @validator()
  arrayMaxLength(length: number, array: any[]): InternalFormValidationBuilder {
    if (array && array.length > length) {
      throw new Error(
        this.i18n
          .t('this field can not have more than {count} values', {
            count: length
          })
          .toString()
      )
    }
    return this
  }

  @validator()
  public maxChars(
    length: number,
    str: string | number
  ): InternalFormValidationBuilder {
    const strLength = str ? str.toString().length : null
    if ((length || length === 0) && strLength && strLength > length) {
      throw new Error(this.i18n.tc('max required characters {count}', length))
    }
    return this
  }

  @validator()
  public urlIsValid(url: string): InternalFormValidationBuilder {
    if (
      !new RegExp(
        "(?:[%:\\w]*@)?(?:(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|[a-z0-9][a-z0-9\\-]{0,62}\\.[a-z]{2,63}|xn--[a-z0-9\\-]{2,59})(?::[0-9]{1,5})?[a-z0-9\\-\\._~:/\\?#\\[\\]@!%\\$&\\'\\(\\)\\*\\+,;=]*$"
      ).test(url.toLowerCase())
    ) {
      throw new Error(this.i18n.t('please enter a valid link').toString())
    }
    return this
  }

  @validator()
  public youtubeLinkIsValid(link: string): InternalFormValidationBuilder {
    if (
      link &&
      !/^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/[^\s]+$/gm.test(link)
    ) {
      throw new Error(
        this.i18n.t('please enter a valid youtube link').toString()
      )
    }
    return this
  }

  @validator()
  public postcodeIsValid(
    postcode: string,
    postcodeValidationRegex: RegExp
  ): InternalFormValidationBuilder {
    if (postcodeValidationRegex && !postcodeValidationRegex.test(postcode)) {
      throw new Error(this.i18n.t('please enter a valid postcode').toString())
    }
    return this
  }

  @validator()
  public passwordIsValid(password: string): InternalFormValidationBuilder {
    if (!/^\S*$/.test(password)) {
      throw new Error(this.i18n.t('must not contain spaces').toString())
    } else if (password.length < 8) {
      throw new Error(
        this.i18n.t('it has to be at least 8 characters long').toString()
      )
    } else if (
      !/^[a-zA-Z0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/.test(password)
    ) {
      throw new Error(this.i18n.t('use only latin characters').toString())
    } else if (!password.match(/\d/)) {
      throw new Error(this.i18n.t('must contain numeric digits').toString())
    } else if (!password.match(/.*[a-zA-Z].*/)) {
      throw new Error(this.i18n.t('must contain letters').toString())
    }
    return this
  }

  @validator()
  public doesNotContainChars(
    forbiddenCharacters: string[],
    value: string
  ): InternalFormValidationBuilder {
    const invalidCharsSet = new Set(forbiddenCharacters)
    for (const char of value) {
      if (invalidCharsSet.has(char)) {
        throw new Error(
          this.i18n
            .t('must not contain {value}', { value: forbiddenCharacters })
            .toString()
        )
      }
    }
    return this
  }

  @validator()
  public hasExactLength(exactLength: number, value: string) {
    if (value.length === 0) return this
    if (value.length !== exactLength) {
      throw new Error(
        this.i18n.tc('exact character length {value}', exactLength).toString()
      )
    }
    return this
  }
}
